/**
 * ESC/POS command primitives (F-FE-N105).
 *
 * The byte layer only. It knows how to say "centre this", "make it bold", "cut"
 * — it knows nothing about receipts. Layout lives in htmlToEscpos.ts and the
 * documents themselves stay HTML.
 *
 * Encoding is ISO-8859-1, matching what the Android agent already does
 * (`ThermalPrinter.printRawText`, `PrintIntentActivity.textToEscPos`): char
 * codes 0-255 map to bytes 0-255 exactly, so what we compute here is what the
 * print head receives. Anything outside that range would print as garbage, so
 * `sanitize` transliterates first — see the note there.
 */

const ESC = 0x1b
const GS = 0x1d

export type Align = 'left' | 'center' | 'right'

/** Accumulates ESC/POS bytes. */
export class EscposBuilder {
  private parts: number[] = []

  raw(...bytes: number[]): this {
    this.parts.push(...bytes)
    return this
  }

  bytes(b: Uint8Array): this {
    for (let i = 0; i < b.length; i++) this.parts.push(b[i])
    return this
  }

  /** ESC @ — reset to a known state. Always the first thing we send. */
  init(): this {
    return this.raw(ESC, 0x40)
  }

  /**
   * GS L / GS W — left margin zero, print area the full head (F-FE-N107).
   *
   * `ESC @` restores the *character* state (bold, size, alignment) but on much
   * firmware it does NOT reset the left margin or the print-area width. Those
   * are sticky, and this printer is shared: at Mizaj it was driven by RawBT
   * before it was driven by us. A margin left behind by another app shifts
   * every line right and pushes the last characters of a full-width row off the
   * paper — which is read at the counter as "the receipt is cut at the sides",
   * with nothing wrong in what we sent.
   *
   * Both commands are ignored by printers that do not implement them, so this
   * costs four bytes and removes a whole class of "works on my printer".
   */
  printArea(dots: number, leftDots = 0): this {
    const d = Math.max(0, Math.min(0xffff, Math.round(dots)))
    const l = Math.max(0, Math.min(0xffff, Math.round(leftDots)))
    return this.raw(GS, 0x4c, l & 0xff, (l >> 8) & 0xff).raw(GS, 0x57, d & 0xff, (d >> 8) & 0xff)
  }

  align(a: Align): this {
    return this.raw(ESC, 0x61, a === 'center' ? 1 : a === 'right' ? 2 : 0)
  }

  bold(on: boolean): this {
    return this.raw(ESC, 0x45, on ? 1 : 0)
  }

  underline(on: boolean): this {
    return this.raw(ESC, 0x2d, on ? 1 : 0)
  }

  /**
   * GS ! — character magnification, 1-8 in each axis.
   *
   * Width doubling HALVES the columns available, which is why the caller must
   * account for it rather than this layer guessing: a double-width line laid out
   * for 48 columns silently wraps and ruins the alignment of everything under it.
   */
  size(width: number, height: number): this {
    const w = Math.max(1, Math.min(8, width)) - 1
    const h = Math.max(1, Math.min(8, height)) - 1
    return this.raw(GS, 0x21, (w << 4) | h)
  }

  text(s: string): this {
    const clean = sanitize(s)
    for (let i = 0; i < clean.length; i++) {
      this.parts.push(clean.charCodeAt(i) & 0xff)
    }
    return this
  }

  line(s = ''): this {
    return this.text(s).raw(0x0a)
  }

  feed(lines = 1): this {
    return this.raw(ESC, 0x64, Math.max(0, Math.min(255, lines)))
  }

  /**
   * GS V 66 n — feed n and partial cut.
   *
   * Partial rather than full: a full cut on a printer without an auto-cutter is
   * a no-op, but a partial leaves the slip attached by a nib on the ones that
   * only support that, which is tidier than the receipt dropping on the floor.
   */
  cut(feedBefore = 4): this {
    return this.feed(feedBefore).raw(GS, 0x56, 66, 0)
  }

  build(): Uint8Array {
    return Uint8Array.from(this.parts)
  }
}

/**
 * Make a string printable on a Latin-1 thermal head.
 *
 * Receipts are full of typography a print head cannot render: the minus sign in
 * "−250.00" is U+2212, not a hyphen; prices may carry ₨; the HTML uses curly
 * quotes and en-dashes. Every one of those becomes '?' if passed through raw,
 * and a receipt reading "?250.00" beside a total is worse than an ugly one.
 *
 * Transliterate first, then drop anything still out of range.
 */
export function sanitize(s: string): string {
  const mapped = (s ?? '')
    .replace(/[−–—‐‑─]/g, '-') // minus, en/em dash, hyphens, box-drawing rule
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/ /g, ' ') // nbsp — invisible but not a space to the head
    .replace(/[₨₹]/g, 'Rs') // ₨ / ₹
    .replace(/[•★⭐]/g, '*')
    .replace(/[×✕✖]/g, 'x')
    .replace(/✓|✔/g, 'v')
    // F-FE-N107 — the rest of what the receipt builders actually emit. Each of
    // these printed as '?' until now: a line-item note led by '?' instead of the
    // continuation arrow, and a Z-report cash column headed '?' where the manager
    // expects the variance.
    .replace(/→/g, '->')
    .replace(/↳/g, '>') // note / modifier continuation marker
    .replace(/Δ/g, 'Diff') // Z-report variance column header
    .replace(/☑/g, '[x]')
    .replace(/☐/g, '[ ]')
    // Pictographs are decoration beside a phone number or a URL. Dropping them
    // leaves the line readable; '?' does not.
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, '')
  // Anything left above 0xFF cannot be represented; '?' is the printer's own
  // substitute, so matching it keeps behaviour predictable.
  let out = ''
  for (const ch of mapped) {
    const c = ch.codePointAt(0) ?? 63
    out += c <= 0xff ? ch : '?'
  }
  return out
}

/** Width of one font-A character cell, in printer dots. */
export const CHAR_DOTS = 12

/** Dots the print head covers for a given roll: 576 on 80mm, 384 on 58mm. */
export function headDotsForPaper(paperWidthMm: number): number {
  return paperWidthMm >= 76 ? 576 : 384
}

/** Columns available at font A for a given paper width. */
export function columnsForPaper(paperWidthMm: number): number {
  // 80mm heads print 48 columns at font A, 58mm heads 32. Anything unusual
  // rounds DOWN to the narrower option — text that is too narrow looks odd,
  // text that is too wide wraps and destroys every aligned column under it.
  return paperWidthMm >= 76 ? 48 : 32
}

/**
 * The usable text box: columns, and the left inset that centres them
 * (F-FE-N110).
 *
 * A full 48 columns is 576 dots, which is the ENTIRE head — the first and last
 * characters sit hard against the edges of the printable area with nothing to
 * spare. A roll seated a millimetre off centre then loses the first character of
 * every left-aligned line, which is exactly what Mizaj's slips showed: `otal
 * Qty`, `escription`, the right-hand column comfortably clear of the edge.
 *
 * Software cannot print left of dot zero, so it cannot correct a misfed roll —
 * but it can decline to use the two cells most likely to fall off it. One
 * character of margin each side costs 4% of the line and makes the output
 * robust on every printer instead of only a perfectly aligned one.
 */
export function textBoxForPaper(paperWidthMm: number): {
  columns: number
  leftDots: number
  widthDots: number
} {
  const head = headDotsForPaper(paperWidthMm)
  const columns = Math.max(1, Math.floor(head / CHAR_DOTS) - 2)
  const widthDots = columns * CHAR_DOTS
  return { columns, leftDots: Math.floor((head - widthDots) / 2), widthDots }
}

/**
 * A label on the left and a value hard right, on one line.
 *
 * The single most common row on a receipt ("Subtotal ....... 1,250.00"), and the
 * one place where getting the arithmetic wrong is most visible: if the pair does
 * not fit, the value must survive and the LABEL is truncated, because a customer
 * can infer a clipped word and cannot infer a clipped number.
 */
export function justify(left: string, right: string, columns: number): string {
  const l = sanitize(left).trim()
  const r = sanitize(right).trim()
  if (!r) return l.slice(0, columns)
  const room = columns - r.length - 1
  if (room <= 0) return r.slice(-columns)
  const cut = l.length > room ? l.slice(0, room) : l
  return cut + ' '.repeat(Math.max(1, columns - cut.length - r.length)) + r
}

/** Wrap to the column width on word boundaries, breaking only unbreakable runs. */
export function wrap(s: string, columns: number): string[] {
  const words = sanitize(s).replace(/\s+/g, ' ').trim()
  if (!words) return []
  const out: string[] = []
  let cur = ''
  for (const w of words.split(' ')) {
    if (w.length > columns) {
      if (cur) {
        out.push(cur)
        cur = ''
      }
      for (let i = 0; i < w.length; i += columns) out.push(w.slice(i, i + columns))
      continue
    }
    if (!cur) cur = w
    else if (cur.length + 1 + w.length <= columns) cur += ' ' + w
    else {
      out.push(cur)
      cur = w
    }
  }
  if (cur) out.push(cur)
  return out
}

/** One cell of a table row: its text, how it aligns, and its share of the row. */
export interface RowCell {
  text: string
  align: Align
  /**
   * Share of the row's width this cell occupies. Taken from the cell's RENDERED
   * width so `colspan` and the CSS percentages are honoured without this layer
   * knowing either exists. Need not sum to 1 — the values are normalised.
   */
  fraction: number
  /**
   * This cell's text must stay whole (the receipt's money columns are
   * `white-space: nowrap`).
   *
   * Without it a narrow column wraps INSIDE a figure: on a 32-column roll the
   * price column comes out 5 wide and `850.00` prints as `850.0` with a lone
   * `0` on the next line. A wrapped price is worse than an ugly receipt — it is
   * a different number. Such a cell claims the width its content needs and the
   * description gives way, the same trade `justify` makes.
   */
  noWrap?: boolean
}

/** Index of the widest column, first one on a tie. */
function widestIndex(widths: number[]): number {
  let best = 0
  for (let i = 1; i < widths.length; i++) if (widths[i] > widths[best]) best = i
  return best
}

/** Pad `s` to `width`, clipping from the end if it is too long. */
export function padAlign(s: string, width: number, align: Align): string {
  if (width <= 0) return ''
  const t = s.length > width ? s.slice(0, width) : s
  const slack = width - t.length
  if (align === 'right') return ' '.repeat(slack) + t
  if (align === 'center') {
    const l = Math.floor(slack / 2)
    return ' '.repeat(l) + t + ' '.repeat(slack - l)
  }
  return t + ' '.repeat(slack)
}

/**
 * Lay a table row across the paper as aligned columns (F-FE-N107).
 *
 * The receipt's line items are a real `<table>` — five columns of #, name,
 * price, qty and total, plus note / modifier / discount rows that span. Before
 * this the ESC/POS renderer had no concept of a row, so it walked down to the
 * text and printed EVERY CELL ON ITS OWN LINE: an item name, then its price
 * alone on the next line, then its quantity alone under that. Mizaj's first
 * tablet receipts came out that way — the price column reading as a stack of
 * loose numbers pinned to the right edge, which is what "it cuts off at the
 * sides" looked like from behind the counter.
 *
 * Widths come from the caller as fractions of the row, so this stays pure and
 * the DOM half stays thin. Columns are separated by `gap` spaces and the widths
 * are made to total EXACTLY the paper width: a row that is a character short on
 * some lines and not others makes the money column wander, which looks broken
 * even when every figure is right.
 *
 * A cell whose text does not fit wraps and the row grows taller, matching the
 * `vertical-align: top` the HTML uses. Text is never silently dropped.
 */
export function layoutRow(cells: RowCell[], columns: number, gap = 1): string[] {
  const n = cells.length
  if (n === 0 || columns <= 0) return []
  if (n === 1) {
    return wrap(cells[0].text, columns).map((l) => padAlign(l, columns, cells[0].align))
  }

  const available = Math.max(n, columns - gap * (n - 1))
  const total = cells.reduce((s, c) => s + Math.max(0, c.fraction), 0)
  const widths = cells.map((c) =>
    total > 0 ? Math.floor((Math.max(0, c.fraction) / total) * available) : Math.floor(available / n),
  )

  // What each cell must have: nothing if it is empty, its whole text if it may
  // not be broken, otherwise a single column so its content does not vanish.
  const mins = cells.map((c) => {
    const t = sanitize(c.text).trim()
    if (!t) return 0
    return c.noWrap ? t.length : 1
  })
  for (let i = 0; i < n; i++) widths[i] = Math.max(widths[i], mins[i])

  let sum = widths.reduce((a, b) => a + b, 0)
  // Over budget: take from whichever column has the most room above its
  // minimum, so the description shrinks before a figure is ever touched.
  while (sum > available) {
    let pick = -1
    let slack = 0
    for (let i = 0; i < n; i++) {
      if (widths[i] - mins[i] > slack) {
        slack = widths[i] - mins[i]
        pick = i
      }
    }
    // Nothing left to give: the row cannot fit even at its minimums (a very
    // narrow roll). Shave the widest so the line at least stays on the paper.
    if (pick < 0) pick = widestIndex(widths)
    if (widths[pick] <= 1) break
    widths[pick]--
    sum--
  }
  // Rounding slack goes to the widest column that can actually use it — on a
  // receipt that is the item description, not a fixed-width money column.
  while (sum < available) {
    const wrappable = widths.map((w, i) => (cells[i].noWrap ? -1 : w))
    const pick = wrappable.some((w) => w >= 0) ? widestIndex(wrappable) : widestIndex(widths)
    widths[pick]++
    sum++
  }

  const wrapped = cells.map((c, i) =>
    c.noWrap ? [sanitize(c.text).trim()].filter(Boolean) : wrap(c.text, widths[i]),
  )
  const height = Math.max(1, ...wrapped.map((w) => w.length))
  const out: string[] = []
  for (let r = 0; r < height; r++) {
    let line = ''
    for (let i = 0; i < n; i++) {
      if (i > 0) line += ' '.repeat(gap)
      line += padAlign(wrapped[i][r] ?? '', widths[i], cells[i].align)
    }
    // Trailing blanks cost paper feed on nothing; the leading ones are structure.
    out.push(line.replace(/\s+$/, ''))
  }
  return out.every((l) => l === '') ? [] : out
}

/**
 * The divider a CSS border becomes on paper.
 *
 * The receipt draws every separator as a border on an element that also has
 * CONTENT — the section heading rules, the box around the items header, the
 * line above the grand total. Returns '' when there is no border to draw.
 */
export function ruleLine(
  style: 'none' | 'solid' | 'dashed' | 'dotted' | 'double' | string,
  widthPx: number,
  columns: number,
): string {
  if (!style || style === 'none' || style === 'hidden' || widthPx <= 0) return ''
  if (style === 'double' || widthPx >= 2) return '='.repeat(columns)
  if (style === 'dashed' || style === 'dotted') {
    // Alternating, so a callout block (dashed in the CSS) still reads as a
    // different kind of divider from a section rule (solid) on paper.
    return '- '.repeat(Math.ceil(columns / 2)).slice(0, columns).replace(/\s+$/, '')
  }
  return '-'.repeat(columns)
}
